import React, { useState } from 'react'
import Select, { components } from 'react-select'
import styled from 'styled-components'
import Responsive from '../../assets/config/Responsive'
import CheckmarkIcon from '../../assets/icons/JSX/CheckmarkIcon'
import RightArrow from '../../assets/icons/JSX/RightArrow'
import Colors from '../../assets/styles/Colors'
//
//
const Dropdown: React.FC<{
  style?: React.CSSProperties
  isDisabled?: boolean
  menuIsOpen?: boolean | undefined
  isLoading?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  placeholder?: string
  options?: Array<{ label: any; value: any; id?: any }>
  value?: any
  className?: string
  onChange: (_: any) => void
  title?: string
  isMulti?: boolean
  closeMenuOnSelect?: boolean
  error?: boolean
  errorText?: string
  required?: boolean
  onBlur?: (value: any) => void
  filterOption?: (option: any, inputValue: string) => boolean
}> = ({
  style,
  isDisabled = false,
  menuIsOpen = undefined,
  isLoading = false,
  isClearable = false,
  isSearchable = false,
  placeholder = '',
  options,
  value,
  className = 'base-dropdown',
  onChange,
  title = '',
  isMulti = false,
  closeMenuOnSelect = true,
  error = false,
  errorText,
  required = false,
  onBlur,
  filterOption,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const singleValue =
    isMulti === false && options?.find((x) => x?.value === 0)
      ? options.find((x) => x.value === value)
      : options?.find((x) => {
          const optionValue = x?.value
          const currentValue = value

          // Handle null/undefined cases
          if (optionValue == null && currentValue == null) return true
          if (optionValue == null || currentValue == null) return false

          // Convert both to strings safely before calling toLowerCase
          const optionValueStr = String(optionValue).toLowerCase()
          const currentValueStr = String(currentValue).toLowerCase()

          return optionValueStr === currentValueStr
        })

  const handleMenuOpen = () => {
    setIsMenuOpen(true)
  }

  const handleMenuClose = () => {
    setIsMenuOpen(false)
  }

  // add checkmark icon
  const getOptionLabel = (option: { value: any; label: any; id?: any }) => {
    // @ts-ignore
    if (option.value === singleValue?.value) {
      return (
        <GetOptionLabelSpanStyle className="option-label-class">
          <span>{option?.label}</span>
          <span className="icon-class">
            <CheckmarkIconStyle />
          </span>
        </GetOptionLabelSpanStyle>
      )
    } else {
      return option.label
    }
  }

  const customDropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <span>
          <RightArrowStyle $isMenuOpen={isMenuOpen} />
        </span>
      </components.DropdownIndicator>
    )
  }

  return (
    <Wrapper $isMenuOpen={isMenuOpen} $error={error} style={style} className="dropdown-wrapper">
      <Title className="dropdown-title-span" $error={error}>
        {title}
      </Title>
      <Select
        menuPlacement="auto"
        placeholder={placeholder || ''}
        className={`${className}`}
        classNamePrefix="v2-select" // don't change this className
        value={isMulti ? value : singleValue || undefined}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isRtl={false}
        menuIsOpen={menuIsOpen}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        isSearchable={isSearchable}
        onChange={(data) => onChange(data)}
        options={options}
        getOptionLabel={getOptionLabel}
        filterOption={filterOption}
        components={{ DropdownIndicator: customDropdownIndicator }}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        required={required}
        onBlur={onBlur}
      />
      {error === true ? <ErrorMessage $error={error}>{errorText}</ErrorMessage> : null}
    </Wrapper>
  )
}
//
const Title = styled.span<{ $error: boolean }>`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  display: inline-block;
  margin-bottom: 3px;
  font-size: 14px;
  color: ${({ $error }) => ($error === true ? `${Colors.primaryRed}` : `${Colors.nevadaGrey}`)};
`
const GetOptionLabelSpanStyle = styled.span`
  display: flex;
  justify-content: space-between;
  align-content: center;
  /*  */
  &:not(:first-of-type) {
    font-weight: bold;
  }
`
const CheckmarkIconStyle = styled(CheckmarkIcon)`
  width: 16px;
  height: 10px;
  margin-top: -7px;
`
const RightArrowStyle = styled(RightArrow)<{ $isMenuOpen: boolean }>`
  transform: ${({ $isMenuOpen }) => ($isMenuOpen ? 'rotate(-90deg)' : 'rotate(90deg)')};
  width: 14px;
  height: 14px;
  margin-right: 16px;
  margin-top: ${({ $isMenuOpen }) => ($isMenuOpen ? '-7px' : '-5px')};
`
const ErrorMessage = styled.div<{ $error: boolean }>`
  width: inherit;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
  color: ${({ $error }) => ($error === true ? `${Colors.primaryRed}` : `${Colors.primaryGrey}`)};
  margin-top: 5px;
`

const Wrapper = styled.div<{ $isMenuOpen: boolean; $error: boolean }>`
  *:disabled {
    opacity: 0.7 !important;
    background-color: ${Colors.secondaryGrey} !important;
  }

  .v2-select__control--is-disabled {
    background-color: ${Colors.secondaryGrey} !important;
    border-color: ${Colors.secondaryGrey} !important;
    opacity: 0.7 !important;
    cursor: not-allowed !important;

    .v2-select__single-value,
    .v2-select__placeholder {
      color: ${Colors.nevadaGrey} !important;
    }
  }

  .v2-select__placeholder {
    font-size: 14px;
  }

  .v2-select__dropdown-indicator {
    ${Responsive.mobile} {
      pointer-events: none;
    }
  }

  .v2-select__menu-list {
    border: 1px solid ${Colors.nevadaGrey};
    border-radius: 4px !important;
    padding: 12px;
  }

  .v2-select__option--is-selected {
    background-color: transparent;
    color: ${Colors.primaryBlack};
  }

  .v2-select__option--is-focused {
    border-radius: 4px;
    background-color: ${Colors.lightGrey};
    color: ${Colors.primaryBlack};
  }

  .v2-select__menu {
    margin-block: 12px;
    z-index: 4;
  }

  .v2-select__option {
    font-size: 14px;
    padding: 12px;

    &:active {
      background-color: ${Colors.lightGrey};
      color: ${Colors.primaryBlack};
    }
  }

  .v2-select__value-container {
    padding-inline: 24px;
  }

  .v2-select__control {
    outline: none;
    box-shadow: none;
    height: 48px;
    border-color: ${({ $error }) => ($error ? `${Colors.primaryRed}` : `${Colors.secondaryGrey}`)};
  }

  .v2-select__control--menu-is-open {
    border-color: ${({ $error }) => ($error ? `${Colors.primaryRed}` : `${Colors.nevadaGrey}`)};
  }

  .v2-select__control:hover {
    border-color: ${({ $error }) => ($error ? `${Colors.primaryRed}` : `${Colors.nevadaGrey}`)};
  }

  .v2-select__indicator-separator {
    display: none;
  }

  .v2-select__single-value {
    font-size: 14px;

    .icon-class {
      display: none;
    }
  }
`

//
export default Dropdown
